import PropTypes from 'prop-types';
import { Fragment, PureComponent } from 'react';
import { connect } from 'react-redux';
import { appointmentActions, appointmentDraftSelectors, bookingsApi } from '~/api';
import {
  apiCallFinished,
  envConfigSelectors,
  errorMessagesActions,
  Messages,
  questionHelpers,
  ReviewForm,
  ReviewIntro,
  ReviewDetails,
  ScreenNav,
  withProgressBar,
  withRequiredPrecondition,
  withRestorableDraft,
} from '~/components';
import { errorTypes } from '~/components/messages';
import {
  getTokenFromStorage,
  removeTokenFromStorage,
  TEMPORARY_TOKEN_NAME,
} from '~/components/persist-token/persist-token.utils';
import { restoreAppointmentData } from '~/routes/helpers';
import { saveTemporaryAppointment, setEditMode } from '../edit';
import styles from './Review.scss';
import { isFeatureEnabled } from '~/components/features/utils';
import { FeaturesContext } from '~/components/features/with-features.hoc';
import { ENV_VARS } from 'ENV_CONFIG';

const womensStylistAppointmentId = ENV_VARS.REACT_APP_WOMENS_STYLIST_APPOINTMENT_ID;
const womensStylistWardrobeAppointmentId =
  ENV_VARS.REACT_APP_WOMENS_STYLIST_WARDROBE_APPOINTMENT_ID;
const nurseryInStoreAppointmentId = ENV_VARS.REACT_APP_NURSERY_45_MIN_INSTORE_APPOINTMENT_ID;
const { APPOINTMENT_NO_LONGER_AVAILABLE_REVIEW_ERROR } = errorTypes;

export class Review extends PureComponent {
  state = {
    isAjaxCompleted: false,
  };

  static contextType = FeaturesContext;

  componentDidMount() {
    const { dispatchRestoreAppointmentData, dispatchApiCallFinished, dispatchClearErrorMessages } =
      this.props;
    this.exitEditMode();

    dispatchApiCallFinished(false);
    dispatchClearErrorMessages();
    // Pass false for termsConditions and empty object for analyticsQuestionSection
    // These will be initialized in restoreAppointmentData
    dispatchRestoreAppointmentData(false, {}).then(() => {
      this.setState({
        isAjaxCompleted: true,
      });
      dispatchApiCallFinished(true);
    });

    window.scrollTo(0, 0);
  }

  exitEditMode = () => {
    const { dispatchSetEditMode, dispatchSaveTemporaryAppointment } = this.props;
    removeTokenFromStorage(TEMPORARY_TOKEN_NAME);
    dispatchSetEditMode(false);
    dispatchSaveTemporaryAppointment({});
  };

  onSubmit = (formValues) => {
    const {
      history,
      questions,
      address,
      dispatchApiCallFinished,
      shop,
      appointmentTypeId,
      dispatchPushSectionErrorToErrorMessage,
      sectionsErrors,

      dispatchUpdateAppointment,
    } = this.props;

    if (sectionsErrors.length > 0) {
      dispatchPushSectionErrorToErrorMessage();
      return null;
    }

    dispatchApiCallFinished(false);

    const handleBookAppointmentSuccess = ({ id }) => {
      removeTokenFromStorage();
      history.push(`/bookings/${id}`);
    };

    const handleBookAppointmentError = (response) => {
      const { status } = response;
      if (status !== 409) {
        return Promise.reject(response);
      }
    };

    return dispatchUpdateAppointment({
      answers: questionHelpers.getFormAnswers(questions, formValues, address, appointmentTypeId),
    })
      .then(() => bookingsApi.bookAppointment(shop.id, appointmentTypeId, getTokenFromStorage()))
      .then(handleBookAppointmentSuccess, handleBookAppointmentError)
      .catch((error) => console.error(error));
  };

  render() {
    const {
      prevPath,
      history,
      questions,
      location: { pathname },
      restoredAnswers,
      config: { ReviewForm: reviewFormConfig, questionsSectionsConfig },
      shop,
      reviewIntroConfig,
      appointmentTypeId,
    } = this.props;
    const { isAjaxCompleted } = this.state;

    const { features = [] } = this.context || {};
    const serviceAppointmentIds = [
      womensStylistAppointmentId,
      womensStylistWardrobeAppointmentId,
      nurseryInStoreAppointmentId,
    ];
    const isWomenStylistEnabled =
      isFeatureEnabled(features, 'womens_stylist') &&
      serviceAppointmentIds.includes(appointmentTypeId);
    const isNurseryChangeEnabled =
      isFeatureEnabled(features, 'nursery_review') &&
      serviceAppointmentIds.includes(appointmentTypeId);
    const modifiedReviewIntroConfig =
      isWomenStylistEnabled || isNurseryChangeEnabled
        ? {
            title: 'Review',
            description: 'Check your details before completing your booking',
          }
        : reviewIntroConfig;

    const reviewCommonProps = {
      initialValues: restoredAnswers,
      questions: questions,
      pathname: pathname,
      history: history,
      questionsSectionsConfig: questionsSectionsConfig,
      prevPath: prevPath,
      ...reviewFormConfig,
    };

    return (
      <Fragment>
        {isAjaxCompleted ? (
          <ReviewForm
            isAjaxCompleted={isAjaxCompleted}
            onSubmit={this.onSubmit}
            {...reviewCommonProps}
          />
        ) : null}
        <section data-testid="Review" className={styles.Review} data-test-ajax={isAjaxCompleted}>
          <Messages forTypes={[APPOINTMENT_NO_LONGER_AVAILABLE_REVIEW_ERROR]} />
          <ReviewIntro reviewIntroConfig={modifiedReviewIntroConfig} shopName={shop.name} />
          {isAjaxCompleted ? (
            <>
              <ReviewDetails {...reviewCommonProps} />
            </>
          ) : null}
        </section>
        <ScreenNav
          hideNextButton
          prevPath={prevPath}
          nextButtonText="Book"
          submit
          formId="ReviewForm"
        />
      </Fragment>
    );
  }
}

export const mapStateToProps = (state) => {
  const {
    questions: { questions, restoredAnswers },
    appointment: {
      draft: {
        appointmentType: { id: appointmentTypeId },
        shop,
      },
    },
    services: { config },
    messages: {
      app: { sectionsErrors },
    },
  } = state;

  return {
    address: appointmentDraftSelectors.selectAddress(state),
    questions,
    appointmentTypeId,
    shop,
    restoredAnswers,
    config,
    sectionsErrors,
    prevPath: config.reviewPrevPath,
    reviewIntroConfig: envConfigSelectors.selectReviewIntro(appointmentTypeId)(state),
  };
};

const mapDispatchToProps = {
  dispatchRestoreAppointmentData: restoreAppointmentData,
  dispatchApiCallFinished: apiCallFinished,
  dispatchResetErrorMessagesWith: errorMessagesActions.resetErrorMessagesWith,
  dispatchClearErrorMessages: errorMessagesActions.clearErrorMessages,
  dispatchPushSectionErrorToErrorMessage: errorMessagesActions.pushSectionErrorToErrorMessage,
  dispatchSetEditMode: setEditMode,
  dispatchSaveTemporaryAppointment: saveTemporaryAppointment,
  dispatchUpdateAppointment: appointmentActions.updateAppointment,
};

export const ConnectedReviewPage = withRestorableDraft(
  withRequiredPrecondition(connect(mapStateToProps, mapDispatchToProps)(withProgressBar(Review))),
);

Review.propTypes = {
  history: PropTypes.shape({}).isRequired,
  prevPath: PropTypes.string.isRequired,
  shop: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }).isRequired,
  appointmentTypeId: PropTypes.string.isRequired,
  address: PropTypes.shape({}),
  addressDescription: PropTypes.shape({}),
  config: PropTypes.shape({}).isRequired,
  questions: PropTypes.shape({}).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }).isRequired,
  restoredAnswers: PropTypes.shape({}),
  sectionsErrors: PropTypes.arrayOf(PropTypes.string).isRequired,
  reviewIntroConfig: PropTypes.shape({}),
  dispatchRestoreAppointmentData: PropTypes.func.isRequired,
  dispatchApiCallFinished: PropTypes.func.isRequired,
  dispatchResetErrorMessagesWith: PropTypes.func.isRequired,
  dispatchClearErrorMessages: PropTypes.func.isRequired,
  dispatchPushSectionErrorToErrorMessage: PropTypes.func.isRequired,
  dispatchSetEditMode: PropTypes.func.isRequired,
  dispatchSaveTemporaryAppointment: PropTypes.func.isRequired,
  dispatchUpdateAppointment: PropTypes.func.isRequired,
};

Review.defaultProps = {
  restoredAnswers: null,
  address: null,
  addressDescription: null,
  reviewIntroConfig: undefined,
};
